import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ListParties } from "../store/slices/parties";
import { AddJournalVoucher } from "../store/slices/journal";
import { Listitems } from "../store/slices/items";
import Select from "react-select";
import { useDispatch } from "react-redux";
import AdminLayout from "./AdminLayout";
import { toast } from "react-hot-toast";

import Loader from "../common/Loader";
import ItemEntryPopup from "../common/ItemEntryPopup";

const Journal = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [listParties, setListParties] = useState([]);
  const [listItems, setListItems] = useState([]);
  const user = JSON.parse(localStorage.getItem("user"));
  const id = user?.data?.profile_id;
  const currentDate = new Date().toISOString().split("T")[0];

  // Main form state
  const [formData, setFormData] = useState({
    profile_id: id,
    voucher_date: currentDate,
    voucher_name: "",
    narration: "",
    debit_entries: [], // each entry: { debit_ledger_id, debit_amount, debit_items: [ { item_id, qty, rate, total_amount } ], currentItem: {...} }
    credit_entries: [],
  });

  // Temporary in-progress entries (for the Add More row before pushing into formData)
  const [debitEntry, setDebitEntry] = useState({
    debit_ledger_id: null,
    debit_amount: "",
    items: [],
    currentItem: { item_id: null, qty: "", rate: "", total_amount: 0 },
  });

  const [creditEntry, setCreditEntry] = useState({
    credit_ledger_id: null,
    credit_amount: "",
    items: [],
    currentItem: { item_id: null, qty: "", rate: "", total_amount: 0 },
  });

  const [errors, setErrors] = useState({});

  // Popup state management
  const [itemPopup, setItemPopup] = useState({
    isOpen: false,
    type: "", // 'debit' or 'credit'
    entryIndex: null, // for adding to existing entries
    title: "Add Item",
  });

  // Convert party/items to Select options (and normalize ids to numbers)
  const partyOptions = listParties.map((party) => ({
    value: Number(party.id),
    label: party.ledger,
  }));

  const itemOptions = listItems.map((item) => ({
    value: Number(item.id),
    label: item.name,
  }));

  // Fetch parties
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    dispatch(ListParties({ profile_id: id }))
      .unwrap()
      .then((data) => {
        setListParties(data?.data || []);
        setIsLoading(false);
      })
      .catch(({ message }) => {
        console.error(message);
        setIsLoading(false);
      });
  }, [dispatch, id]);

  // Fetch items
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    dispatch(Listitems({ profile_id: id }))
      .unwrap()
      .then((data) => {
        setListItems(data?.data || []);
        setIsLoading(false);
      })
      .catch(({ message }) => {
        console.error(message);
        setIsLoading(false);
      });
  }, [dispatch, id]);

  // Generic input change for voucher_date and voucher_name
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  // Remove entry
  const removeDebitEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      debit_entries: prev.debit_entries.filter((_, i) => i !== index),
    }));
  };

  const removeCreditEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      credit_entries: prev.credit_entries.filter((_, i) => i !== index),
    }));
  };

  // Edit debit/credit entry fields (ledger or amount)
  const handleDebitEdit = (index, field, value) => {
    setFormData((prev) => {
      const updated = prev.debit_entries.map((entry, i) => (i === index ? { ...entry, [field]: field.includes("ledger") ? Number(value) : value } : entry));
      return { ...prev, debit_entries: updated };
    });
  };

  const handleCreditEdit = (index, field, value) => {
    setFormData((prev) => {
      const updated = prev.credit_entries.map((entry, i) => (i === index ? { ...entry, [field]: field.includes("ledger") ? Number(value) : value } : entry));
      return { ...prev, credit_entries: updated };
    });
  };


  // Add currentItem to an existing entry's items
  const addItemToEntry = (entryType, entryIndex, itemData = null) => {
    setFormData((prev) => {
      const entries = [...prev[`${entryType}_entries`]];
      const entry = { ...entries[entryIndex] };
      const item = itemData || { ...entry.currentItem };

      if (!item.item_id || !item.qty || !item.rate) {
        toast.error("Item, quantity and rate are required to add an item.");
        return prev;
      }

      const updatedItems = [...(entry[`${entryType}_items`] || []), item];
      entry[`${entryType}_items`] = updatedItems;
      // reset entry's currentItem only if we're using currentItem (not itemData)
      if (!itemData) {
        entry.currentItem = { item_id: null, qty: "", rate: "", total_amount: 0 };
      }
      entries[entryIndex] = entry;
      return { ...prev, [`${entryType}_entries`]: entries };
    });
  };

  // ---------- Popup handlers ----------
  const openItemPopup = (type) => {
    setItemPopup({
      isOpen: true,
      type: type,
      entryIndex: null,
      title: `Add Item to ${type === "debit" ? "Debit" : "Credit"} Entry`,
    });
  };

  const closeItemPopup = () => {
    setItemPopup({
      isOpen: false,
      type: "",
      entryIndex: null,
      title: "Add Item",
    });
  };

  const handleItemSave = (itemData) => {
    if (itemPopup.entryIndex !== null) {
      // Adding to an existing entry
      addItemToEntry(itemPopup.type, itemPopup.entryIndex, itemData);
    } else {
      // Adding to temporary entry
      if (itemPopup.type === "debit") {
        addItemToTempEntry_v2(true, itemData);
      } else if (itemPopup.type === "credit") {
        addItemToTempEntry_v2(false, itemData);
      }
    }
    closeItemPopup();
  };

  // Modified addItemToTempEntry to accept itemData parameter
  const addItemToTempEntry_v2 = (isDebit, itemData = null) => {
    if (isDebit) {
      const item = itemData || { ...debitEntry.currentItem };
      if (!item.item_id || !item.qty || !item.rate) {
        toast.error("Item, quantity and rate are required to add an item.");
        return;
      }
      setDebitEntry((prev) => ({
        ...prev,
        items: [...(prev.items || []), item],
        currentItem: { item_id: null, qty: "", rate: "", total_amount: 0 },
      }));
    } else {
      const item = itemData || { ...creditEntry.currentItem };
      if (!item.item_id || !item.qty || !item.rate) {
        toast.error("Item, quantity and rate are required to add an item.");
        return;
      }
      console.log("Adding item to credit entry:", item);
      setCreditEntry((prev) => {
        const newEntry = {
          ...prev,
          items: [...(prev.items || []), item],
          currentItem: { item_id: null, qty: "", rate: "", total_amount: 0 },
        };
        console.log("Updated credit entry:", newEntry);
        return newEntry;
      });
    }
  };

  // Remove item from an existing entry
  const removeItem = (entryType, entryIndex, itemIndex) => {
    setFormData((prev) => {
      const entries = [...prev[`${entryType}_entries`]];
      const entry = { ...entries[entryIndex] };
      entry[`${entryType}_items`] = (entry[`${entryType}_items`] || []).filter((_, j) => j !== itemIndex);
      entries[entryIndex] = entry;
      return { ...prev, [`${entryType}_entries`]: entries };
    });
  };


  const addDebitEntry_v2 = () => {
    if (!debitEntry.debit_ledger_id) {
      toast.error("Select a Debit Ledger before adding.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      debit_entries: [
        ...prev.debit_entries,
        {
          debit_ledger_id: Number(debitEntry.debit_ledger_id),
          debit_amount: debitEntry.debit_amount || "",
          debit_items: debitEntry.items ? [...debitEntry.items] : [],
          currentItem: { item_id: null, qty: "", rate: "", total_amount: 0 },
        },
      ],
    }));

    setDebitEntry({
      debit_ledger_id: null,
      debit_amount: "",
      items: [],
      currentItem: { item_id: null, qty: "", rate: "", total_amount: 0 },
    });
  };

  const addCreditEntry_v2 = () => {
    if (!creditEntry.credit_ledger_id) {
      toast.error("Select a Credit Ledger before adding.");
      return;
    }

    console.log("Adding credit entry with items:", creditEntry.items);

    setFormData((prev) => ({
      ...prev,
      credit_entries: [
        ...prev.credit_entries,
        {
          credit_ledger_id: Number(creditEntry.credit_ledger_id),
          credit_amount: creditEntry.credit_amount || "",
          credit_items: creditEntry.items ? [...creditEntry.items] : [],
          currentItem: { item_id: null, qty: "", rate: "", total_amount: 0 },
        },
      ],
    }));

    setCreditEntry({
      credit_ledger_id: null,
      credit_amount: "",
      items: [],
      currentItem: { item_id: null, qty: "", rate: "", total_amount: 0 },
    });
  };

  const formatCurrency = (amount) => {
    const val = Number(amount) || 0;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    })
      .format(val)
      .replace("₹", "₹ ");
  };

  // Totals: handle both entries and item totals (if you want to include item totals in amounts you can do so)
  const totalDebitAmount = (formData.debit_entries || []).reduce((total, entry) => {
    return total + (parseFloat(entry.debit_amount || 0) || 0);
  }, 0);

  const totalCreditAmount = (formData.credit_entries || []).reduce((total, entry) => {
    return total + (parseFloat(entry.credit_amount || 0) || 0);
  }, 0);

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.voucher_name) newErrors.voucher_name = "Voucher Name is required.";
    if (!formData.voucher_date) newErrors.voucher_date = "Voucher date is required.";

    if (!formData.debit_entries || formData.debit_entries.length === 0) {
      newErrors.debit_entries = "At least one debit entry is required.";
      toast.error("At least one debit entry is required.");
    }

    if (!formData.credit_entries || formData.credit_entries.length === 0) {
      newErrors.credit_entries = "At least one credit entry is required.";
      toast.error("At least one credit entry is required.");
    }

    // numeric equality check (use a tolerance for floating point)
    const diff = Math.abs(Number(totalDebitAmount) - Number(totalCreditAmount));
    if (diff > 0.0001) {
      newErrors.mismatch = "Total Debit and Credit amounts must be equal.";
      toast.error("Debit and Credit entries does not match.");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  function cleanFormData(formData) {
    return {
      ...formData,
      debit_entries: formData.debit_entries.map(({ currentItem, ...rest }) => rest),
      credit_entries: formData.credit_entries.map(({ currentItem, ...rest }) => rest),
    };
  }

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    console.log("Form is valid", formData);
    let cleanedData = cleanFormData(formData);
    console.log("Submitting form data:", cleanedData);
    setIsLoading(true);
    try {
      await dispatch(AddJournalVoucher(cleanedData)).unwrap();
      setIsLoading(false);
      navigate("/journallist");
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      toast.error(err?.message || "Failed to add voucher");
    }
  };

  // ---------- JSX Render ----------
  return (
    <AdminLayout>
      {isLoading && <Loader />}
      <div className="row content-body">
        <div className="container">
          <div className="page-header">
            <div>
              <h2 className="main-content-title tx-24 mg-b-5">Journal Voucher</h2>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#">Journal Voucher</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Add Journal Voucher
                </li>
              </ol>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="btn ripple btn-default" onClick={handleSubmit}>
                Save
              </button>
            </div>
          </div>

          {/* Voucher date & name */}
          <div className="row">
            <div className="col-md-12">
              <div className="card custom-card">
                <div className="card-body">
                  <div className="form-group">
                    <div className="row">
                      <div className="col-md-3">
                        <label>
                          Voucher Date <span className="required">*</span>
                        </label>
                        <input name="voucher_date" type="date" className="form-control" value={formData?.voucher_date} onChange={handleInputChange} />
                        {errors?.voucher_date && <p className="alert-message">{errors?.voucher_date}</p>}
                      </div>
                      <div className="col-md-3">
                        <label>
                          Voucher Name <span className="required">*</span>
                        </label>
                        <input
                          name="voucher_name"
                          aria-describedby="basic-addon1"
                          required
                          aria-label="Voucher Name"
                          className="form-control"
                          type="text"
                          value={formData.voucher_name}
                          onChange={handleInputChange}
                        />
                        {errors?.voucher_name && <p className="alert-message">{errors?.voucher_name}</p>}
                      </div>
                      <div className="col-md-6">
                        <label>Narration</label>
                        <input
                          name="narration"
                          className="form-control"
                          value={formData.narration}
                          onChange={handleInputChange}
                          placeholder="Enter narration or description"
                        />
                      </div>
                    </div>
                  </div>

                  <hr />

                  {/* DEBIT: Temporary Add row */}
                  <div className="form-group">
                    <div className="row">
                      <div className="col-md-3">
                        <label>
                          Debit Account <span className="required">*</span>
                        </label>
                        <Select
                          options={partyOptions}
                          placeholder="--Select Ledger--"
                          onChange={(opt) => setDebitEntry((prev) => ({ ...prev, debit_ledger_id: opt ? opt.value : null }))}
                          value={partyOptions.find((p) => p.value === debitEntry.debit_ledger_id) || null}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>
                          Amount <span className="required">*</span>
                        </label>
                        <input
                          aria-describedby="basic-addon1"
                          aria-label="Debit Amount"
                          className="form-control"
                          type="text"
                          value={debitEntry.debit_amount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || (!isNaN(value) && Number(value) >= 0)) {
                              setDebitEntry((prev) => ({ ...prev, debit_amount: value }));
                            }
                          }}
                        />
                      </div>

                      <div className="col-md-3">
                        <div style={{ marginTop: 32 }}>
                          {[2, 3, 9].includes(Number(debitEntry.debit_ledger_id)) && (
                            <a href="#!" onClick={() => openItemPopup("debit")} className="me-3 btn btn-outline-primary btn-sm" style={{ marginRight: 8 }}>
                              Add Item
                            </a>
                          )}
                          <a
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              addDebitEntry_v2();
                            }}
                            className="me-3"
                          >
                            Add More
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Display temporary debit items */}
                    {[2, 3, 9].includes(Number(debitEntry.debit_ledger_id)) && (debitEntry.items || []).length > 0 && (
                      <div className="row mb-3 p-3" style={{ marginTop: 12 }}>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0" style={{ color: "#000" }}>Items Added to Current Debit Entry</h6>
                          </div>
                          <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                              <thead style={{ backgroundColor: "#fff3cd" }}>
                                <tr>
                                  <th>Item</th>
                                  <th>Quantity</th>
                                  <th>Rate</th>
                                  <th>Total Cost</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {debitEntry.items.map((it, idx) => (
                                  <tr key={`temp-debit-item-${idx}`}>
                                    <td>{itemOptions.find((o) => o.value === it.item_id)?.label || "-"}</td>
                                    <td>{it.qty}</td>
                                    <td>{it.rate}</td>
                                    <td>
                                      <span className="fw-bold" style={{ color: "#28a745" }}>
                                        {formatCurrency(it.total_amount)}
                                      </span>
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => {
                                          setDebitEntry((prev) => ({
                                            ...prev,
                                            items: (prev.items || []).filter((_, i) => i !== idx),
                                          }));
                                        }}
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="alert alert-info" style={{ backgroundColor: "#d1ecf1", border: "1px solid #bee5eb", color: "#0c5460" }}>
                            <small><strong>Note:</strong> These items will be saved when you click "Add More" to complete this debit entry.</small>
                          </div>
                        </div>
                      </div>
                    )}

                    {(formData.debit_entries || []).map((entry, index) => (
                      <div key={`debit-entry-${index}`} className="mb-3 mt-3">
                        <div className="row">
                          <div className="col-md-3">
                            <Select
                              options={partyOptions}
                              placeholder="--Select Ledger--"
                              onChange={(opt) => handleDebitEdit(index, "debit_ledger_id", opt ? opt.value : null)}
                              value={partyOptions.find((p) => p.value === entry.debit_ledger_id) || null}
                            />
                          </div>

                          <div className="col-md-3">
                            <input
                              className="form-control"
                              type="text"
                              value={entry.debit_amount}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "" || (!isNaN(v) && Number(v) >= 0)) handleDebitEdit(index, "debit_amount", v);
                              }}
                            />
                          </div>

                          <div className="col-md-3">
                            <div className="d-flex gap-2">
                                 {[2, 3, 9].includes(Number(entry.debit_ledger_id)) && (
                                <button 
                                  className="btn btn-outline-primary btn-sm" 
                                  onClick={() => {
                                    setItemPopup({
                                      isOpen: true,
                                      type: "debit",
                                      entryIndex: index,
                                      title: `Add Item to Debit Entry ${index + 1}`,
                                      
                                    });
                                  }}
                                  style={{ marginRight: 8 }}
                                >
                                  Add Item
                                </button>
                              )}
                              <button className="btn btn-danger" onClick={() => removeDebitEntry(index)}>
                                Remove
                              </button>
                           
                            </div>
                          </div>
                        </div>

                        {/* Display items for this saved debit entry */}
                        {[2, 3, 9].includes(Number(entry.debit_ledger_id)) && (entry.debit_items || []).length > 0 && (
                          <div className="mt-2 p-2" style={{ backgroundColor: "#f8f9fa", borderRadius: 6 }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="mb-0">Items in this Debit Entry</h6>
                            </div>
                            <div className="table-responsive">
                              <table className="table table-bordered table-hover">
                                <thead style={{ backgroundColor: "#e9ecef" }}>
                                  <tr>
                                    <th>Item</th>
                                    <th>Quantity</th>
                                    <th>Rate</th>
                                    <th>Total Cost</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {entry.debit_items.map((it, itemIdx) => (
                                    <tr key={`debit-${index}-item-${itemIdx}`}>
                                      <td>{itemOptions.find((o) => o.value === it.item_id)?.label || "-"}</td>
                                      <td>{it.qty}</td>
                                      <td>{it.rate}</td>
                                      <td>
                                        <span className="fw-bold" style={{ color: "#28a745" }}>
                                          {formatCurrency(it.total_amount)}
                                        </span>
                                      </td>
                                      <td>
                                        <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem("debit", index, itemIdx)}>
                                          Remove
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
       <hr />
                  {/* CREDIT: Temporary Add row */}
                  <div className="form-group mt-4">
                    <div className="row">
                      <div className="col-md-3">
                        <label>
                          Credit Account <span className="required">*</span>
                        </label>
                        <Select
                          options={partyOptions}
                          placeholder="--Select Ledger--"
                          onChange={(opt) => setCreditEntry((prev) => ({ ...prev, credit_ledger_id: opt ? opt.value : null }))}
                          value={partyOptions.find((p) => p.value === creditEntry.credit_ledger_id) || null}
                        />
                      </div>

                      <div className="col-md-3">
                        <label>Amount</label>
                        <input
                          className="form-control"
                          type="text"
                          value={creditEntry.credit_amount}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "" || (!isNaN(v) && Number(v) >= 0)) setCreditEntry((prev) => ({ ...prev, credit_amount: v }));
                          }}
                        />
                      </div>

                      <div className="col-md-3">
                        <div style={{ marginTop: 32 }}>
                          {[2, 3, 9].includes(Number(creditEntry.credit_ledger_id)) && (
                            <a href="#!" onClick={() => openItemPopup("credit")} className="me-3 btn btn-outline-primary btn-sm" style={{ marginRight: 8 }}>
                              Add Item
                            </a>
                          )}
                          <a
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              addCreditEntry_v2();
                            }}
                            className="me-3"
                          >
                            Add More
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Display temporary credit items */}
                    {[2, 3, 9].includes(Number(creditEntry.credit_ledger_id)) && (creditEntry.items || []).length > 0 && (
                      <div className="row mb-3 p-3" style={{ marginTop: 12 }}>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0" style={{ color: "#000" }}>Items Added to Current Credit Entry</h6>
                          </div>
                          <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                              <thead style={{ backgroundColor: "#d4edda" }}>
                                <tr>
                                  <th>Item</th>
                                  <th>Quantity</th>
                                  <th>Rate</th>
                                  <th>Total Cost</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {creditEntry.items.map((it, idx) => (
                                  <tr key={`temp-credit-item-${idx}`}>
                                    <td>{itemOptions.find((o) => o.value === it.item_id)?.label || "-"}</td>
                                    <td>{it.qty}</td>
                                    <td>{it.rate}</td>
                                    <td>
                                      <span className="fw-bold" style={{ color: "#28a745" }}>
                                        {formatCurrency(it.total_amount)}
                                      </span>
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => {
                                          setCreditEntry((prev) => ({
                                            ...prev,
                                            items: (prev.items || []).filter((_, i) => i !== idx),
                                          }));
                                        }}
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="alert alert-info mt-2" style={{ backgroundColor: "#d1ecf1", border: "1px solid #bee5eb", color: "#0c5460" }}>
                            <small><strong>Note:</strong> These items will be saved when you click "Add More" to complete this credit entry.</small>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Render existing credit entries */}
                    {(formData.credit_entries || []).map((entry, index) => (
                      <div key={`credit-entry-${index}`} className="mb-3 mt-3">
                        <div className="row">
                          <div className="col-md-3">
                            <Select
                              options={partyOptions}
                              placeholder="--Select Ledger--"
                              onChange={(opt) => handleCreditEdit(index, "credit_ledger_id", opt ? opt.value : null)}
                              value={partyOptions.find((p) => p.value === entry.credit_ledger_id) || null}
                            />
                          </div>

                          <div className="col-md-3">
                            <input
                              className="form-control"
                              type="text"
                              value={entry.credit_amount}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === "" || (!isNaN(v) && Number(v) >= 0)) handleCreditEdit(index, "credit_amount", v);
                              }}
                            />
                          </div>

                          <div className="col-md-3">
                            <div className="d-flex gap-2">
                                {[2, 3, 9].includes(Number(entry.credit_ledger_id)) && (
                                <button 
                                  className="btn btn-outline-primary btn-sm" 
                                  onClick={() => {
                                    setItemPopup({
                                      isOpen: true,
                                      type: "credit",
                                      entryIndex: index,
                                      title: `Add Item to Credit Entry ${index + 1}`,
                                    });
                                  }}
                                  style={{ marginRight: 8 }}
                                >
                                  Add Item
                                </button>
                              )}
                              <button className="btn btn-danger" onClick={() => removeCreditEntry(index)}>
                                Remove
                              </button>
                            
                            </div>
                          </div>
                        </div>

                        {/* Display items for this saved credit entry */}
                        {[2, 3, 9].includes(Number(entry.credit_ledger_id)) && (entry.credit_items || []).length > 0 && (
                          <div className="mt-2 p-2" style={{ backgroundColor: "#f8f9fa", borderRadius: 6 }}>
                            <h6 className="mb-2">Items in this Credit Entry</h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-hover">
                                <thead style={{ backgroundColor: "#e9ecef" }}>
                                  <tr>
                                    <th>Item</th>
                                    <th>Quantity</th>
                                    <th>Rate</th>
                                    <th>Total Cost</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {entry.credit_items.map((it, itemIdx) => (
                                    <tr key={`credit-${index}-item-${itemIdx}`}>
                                      <td>{itemOptions.find((o) => o.value === it.item_id)?.label || "-"}</td>
                                      <td>{it.qty}</td>
                                      <td>{it.rate}</td>
                                      <td>
                                        <span className="fw-bold" style={{ color: "#28a745" }}>
                                          {formatCurrency(it.total_amount)}
                                        </span>
                                      </td>
                                      <td>
                                        <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem("credit", index, itemIdx)}>
                                          Remove
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>{" "}
                {/* card-body */}
              </div>{" "}
              {/* card */}
              {/* Totals */}
              <div className="row mt-4 mb-4">
                <div className="col-md-6">
                  <div className="card custom-card">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-12 d-flex">
                          <p className="fw-bolder fs-6" style={{ fontSize: "1rem", fontWeight: "700" }}>
                            Total Debit Amount :
                          </p>
                          <span style={{ fontSize: "1rem", marginLeft: 8 }}>{formatCurrency(totalDebitAmount)}</span>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12 d-flex">
                          <p className="fw-bolder fs-6" style={{ fontSize: "1rem", fontWeight: "700" }}>
                            Total Credit Amount :
                          </p>
                          <span style={{ fontSize: "1rem", marginLeft: 8 }}>{formatCurrency(totalCreditAmount)}</span>
                        </div>
                      </div>

                      {errors?.mismatch && (
                        <p className="alert-message" style={{ color: "red" }}>
                          {errors.mismatch}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Item Entry Popup */}
      <ItemEntryPopup isOpen={itemPopup.isOpen} onClose={closeItemPopup} onSave={handleItemSave} itemOptions={itemOptions} title={itemPopup.title} />
    </AdminLayout>
  );
};

export default Journal;
